import { SensorLockerStatus } from '@frontend/device/types';
import { Logger } from '@frontend/logger';
import { PackageClient, PackageWorkflowClient } from '@frontend/package/api';
import { PackageTriggerName, PackageType } from '@frontend/package/types';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { TransactionActorClient, TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedVendingTransaction, TransactionActorType, TransactionStateName } from '@frontend/transaction/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSlotListener } from '../../../hooks/useSlotListener/useSlotListener';
import { useAppSelector } from '../../../redux/store';
import { useScreen } from '../../screens/useScreen';

export interface SlotInfo {
    slotId: string;
    moduleId: string;
    slot: Slot | null;
}

export type ScreenPhase = 'loading' | 'confirm' | 'processing';

export interface ReviewRecyclesConfirmOpenSlotViewProps {
    phase: ScreenPhase;
    slots: SlotInfo[];
    slotCount: number;
    slotStatusMap: Map<string, SensorLockerStatus>;
    allSlotsClosed: boolean;
    onOpenSlots: () => void;
    onSubmit: () => void;
    submitDisabled: boolean;
}

export const useReviewRecyclesConfirmOpenSlot = (): ReviewRecyclesConfirmOpenSlotViewProps => {
    const navigationState = useAppSelector((state) => state.navigation);
    const { onSubmit } = useScreen();
    const [phase, setPhase] = useState<ScreenPhase>('loading');
    const [transactions, setTransactions] = useState<ExtendedVendingTransaction[]>([]);
    const [resolvedSlots, setResolvedSlots] = useState<Map<string, Slot>>(new Map());
    const [slotStatusMap, setSlotStatusMap] = useState<Map<string, SensorLockerStatus>>(new Map());
    const slotStatusMapRef = useRef<Map<string, SensorLockerStatus>>(new Map());

    const accountId = navigationState.iot?.account_id;
    const spotId = navigationState.spot?.id;
    const userId = navigationState.user?.id;

    useEffect(() => {
        if (!userId || !spotId) {
            setPhase('confirm');
            return;
        }

        let cancelled = false;
        void TransactionVendingClient.getRecycles({
            spot_id: spotId,
            state: [TransactionStateName.TRANSACTION_DROP_OFF_PENDING, TransactionStateName.TRANSACTION_PICK_UP_PENDING],
            size: '1000'
        }).then((response) => {
            if (cancelled) return;
            setTransactions(response.results);
            if (response.results.length === 0) {
                setPhase('confirm');
            }
        });

        return () => {
            cancelled = true;
        };
    }, [userId, spotId]);

    useEffect(() => {
        if (transactions.length === 0) return;

        const slotIds = new Set<string>();
        for (const tx of transactions) {
            if (tx.slot_id) slotIds.add(tx.slot_id);
        }

        if (slotIds.size === 0) {
            setPhase('confirm');
            return;
        }

        let cancelled = false;
        SlotClient.resolveSlots(Array.from(slotIds)).then((response) => {
            if (cancelled) return;
            const slotMap = new Map<string, Slot>();
            for (const slot of response.results) {
                slotMap.set(slot.id, slot);
            }
            setResolvedSlots(slotMap);
            setPhase('confirm');
        });

        return () => {
            cancelled = true;
        };
    }, [transactions]);

    const slots = useMemo((): SlotInfo[] => {
        const slotMap = new Map<string, string>();

        for (const tx of transactions) {
            if (!tx.slot_id || !tx.module_id) continue;
            if (!slotMap.has(tx.slot_id)) {
                slotMap.set(tx.slot_id, tx.module_id);
            }
        }

        return Array.from(slotMap.entries()).map(([slotId, moduleId]) => ({
            slotId,
            moduleId,
            slot: resolvedSlots.get(slotId) ?? null
        }));
    }, [transactions, resolvedSlots]);

    useEffect(() => {
        if (slots.length === 0 || slotStatusMapRef.current.size > 0) return;
        const statusMap = new Map<string, SensorLockerStatus>();
        for (const s of slots) {
            statusMap.set(s.slotId, SensorLockerStatus.CLOSED);
        }
        slotStatusMapRef.current = statusMap;
        setSlotStatusMap(statusMap);
    }, [slots]);

    useSlotListener({
        id: 'REVIEW_RECYCLES_CONFIRM_OPEN_SLOT',
        onPackageSlotOpened(pkg) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.OPENED);
            }
        },
        onPackageSlotNotOpened(pkg) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_OPENED);
            }
        },
        onPackageSlotClosed(pkg) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.CLOSED);
            }
        },
        onPackageSlotNotClosed(pkg) {
            if (pkg.slot_id && slotStatusMapRef.current.has(pkg.slot_id)) {
                setSlotStatusMap((prevMap) => {
                    const newMap = new Map(prevMap);
                    newMap.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
                    return newMap;
                });
                slotStatusMapRef.current.set(pkg.slot_id, SensorLockerStatus.NOT_CLOSED);
            }
        }
    });

    const allSlotsClosed = useMemo(() => {
        if (slotStatusMap.size === 0) return false;
        return Array.from(slotStatusMap.values()).every((status) => status === SensorLockerStatus.CLOSED);
    }, [slotStatusMap]);

    const onOpenSlots = useCallback(async () => {
        if (!accountId) return;

        const statusMap = new Map<string, SensorLockerStatus>();
        for (const s of slots) {
            statusMap.set(s.slotId, SensorLockerStatus.OPENING);
        }
        slotStatusMapRef.current = statusMap;
        setSlotStatusMap(statusMap);
        setPhase('processing');

        for (const s of slots) {
            const tx = transactions.find((t) => t.slot_id === s.slotId);
            if (!tx) continue;

            if (navigationState.user?.id) {
                TransactionActorClient.fetchTransactionActors({
                    transaction_id: tx.id,
                    user_id: navigationState.user.id,
                    type: TransactionActorType.PICKED_UP_BY
                }).then((result) => {
                    if (result.results.length === 0) {
                        TransactionActorClient.postTransactionActor(tx.account_id, tx.id, {
                            user_id: navigationState.user!.id,
                            type: TransactionActorType.PICKED_UP_BY
                        });
                    }
                });
            }

            try {
                const packages = await PackageClient.fetchAccountTransactionPackages(accountId, tx.id);
                const firstPackage = packages.results.filter((pkg) => pkg.type === PackageType.AUDIT)[0];
                if (firstPackage) {
                    await PackageWorkflowClient.triggerPackageState(accountId, tx.id, firstPackage.id, {
                        trigger: PackageTriggerName.PACKAGE_CONTINUE,
                        location: navigationState.spot?.id ?? undefined,
                        actor: navigationState.user?.id ?? undefined
                    });
                }
            } catch (error) {
                Logger.error(`Failed to trigger package open slot for slot ${s.slotId}`, error);
            }
        }
    }, [accountId, slots, transactions]);

    return {
        phase,
        slots,
        slotCount: slots.length,
        slotStatusMap,
        allSlotsClosed,
        onOpenSlots,
        onSubmit,
        submitDisabled: phase === 'loading' || (phase === 'confirm' && slots.length === 0) || (phase === 'processing' && !allSlotsClosed)
    };
};
